import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import {
  ExpandLess, ExpandMore, AccountBalance as HomeIcon, Domain as CenterIcon, LockOutlined as PeriodCloseIcon,
} from '@mui/icons-material';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { ADMIN_ROLES, hasRevRecAccess } from '../../../../profile';
import { REVREC_CENTER_PATH, REVREC_HOME_PATH, REVREC_PERIOD_CLOSE_PATH } from '../../../../revenueRecognition/routes';

const MENU_STYLES = {
    navList: { border: 'black solid 1px' },
    nestedListItem: { marginTop: '0px', paddingLeft: '48px' },
    icon: { color: 'white' },
} as const;

/**
 * "Revenue Recognition" nav group (FR-012, US2) — gated by the **RevRec** claim, on LaborMenu's shape:
 * one collapsible group with the module's entries nested under it, like every other module's group.
 * The entries: the home, the selected center's dashboard and, for the one role that may use it, the
 * period close.
 *
 * The center entry is disabled until the shell has a center, and carries no period query string —
 * the dashboard's own `usePeriodParams` owns the current-month default, and a second copy of that
 * rule here would drift.
 */
function RevenueRecognitionMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: profile } = useGetMyProfileQuery();
  const selectedCenter = useAppSelector((state) => state.center.selectedCenter);

  if (!hasRevRecAccess(profile)) {
    return null;
  }

  return (
    <AuthenticatedTemplate>
      <List component="nav" sx={MENU_STYLES.navList}>
        <ListItemButton onClick={() => setOpen(!open)} data-testid="revrec-menu-group">
          <ListItemText primary="Revenue Recognition" />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <ListItemButton
            sx={MENU_STYLES.nestedListItem}
            onClick={() => navigate(REVREC_HOME_PATH)}
            data-testid="revrec-menu-button"
          >
            <ListItemIcon><HomeIcon sx={MENU_STYLES.icon} /></ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
          <ListItemButton
            sx={MENU_STYLES.nestedListItem}
            onClick={selectedCenter ? () => navigate(REVREC_CENTER_PATH.replace(':center', selectedCenter)) : undefined}
            disabled={!selectedCenter}
            data-testid="revrec-center-menu-button"
          >
            <ListItemIcon><CenterIcon sx={MENU_STYLES.icon} /></ListItemIcon>
            <ListItemText primary="Center Dashboard" />
          </ListItemButton>
          {profile?.role === ADMIN_ROLES.REV_CYCLE_ADMIN && (
            <ListItemButton
              sx={MENU_STYLES.nestedListItem}
              onClick={() => navigate(REVREC_PERIOD_CLOSE_PATH)}
              data-testid="revrec-period-close-menu-button"
            >
              <ListItemIcon><PeriodCloseIcon sx={MENU_STYLES.icon} /></ListItemIcon>
              <ListItemText primary="Period Close" />
            </ListItemButton>
          )}
        </Collapse>
      </List>
    </AuthenticatedTemplate>
  );
}

export { RevenueRecognitionMenu };
